<script setup>
import {onBeforeUnmount, onMounted, ref} from 'vue';
import Button from "../../components/forms/Button.vue";
import {useRouter} from "vue-router";
import DocumentService from "@/services/DocumentService";
import FileView from "@/components/files/FileView.vue";
import {createConfirmDialog} from 'vuejs-confirm-dialog';
import SignModal from '@/components/modals/SignModal.vue';
import {formatDate, showSuccessMessage} from '@/helper';
import BSTooltip from "@/components/tooltips/BSTooltip.vue";
import SignSettingsModal from '@/components/modals/SignSettingsModal.vue';
import IntermediaryPersonsService from '@/services/IntermediaryPersonsService';
import {getLoggedUser} from "@/services/AuthService";
import {useToast} from "vue-toastification";
import {Dialog, DialogPanel, TransitionRoot} from "@headlessui/vue";
import Textarea from "@/components/forms/Textarea.vue";

import {useI18n} from 'vue-i18n'

const {t} = useI18n();

const document = ref(null);
const intermediariesPeople = ref(null);
const router = useRouter();
const documentId = router.currentRoute.value.params.id;
let refreshInterval = ref(null);
const loggedUser = ref(null);
const toast = useToast();
const modalOpen = ref(false);

const stateDescription = ref('');

const canBeAccepted = ref(false);

const isLoading = ref(false);
const isRegenerating = ref(false);

const signatureStateMap = {
  'PENDING': t('documentsDetail.waiting'),
  'SIGNED': t('documentsDetail.signed'),
  'REJECTED': t('documentsDetail.rejected'),
};

const documentStateMap = {
  'PREPARED': t('documentsDetail.prepared'),
  'TO_BE_SIGNED': t('documentsDetail.to_be_signed2'),
  'SIGNED': t('documentsDetail.signed3'),
  'TO_REVIEW': t('documentsDetail.to_review'),
  'ACCEPTED': t('documentsDetail.approved'),
  'CANCELED': t('documentsDetail.canceled2'),
  'REJECTED': t('documentsDetail.rejected2'),
};

onMounted(() => {
  getLoggedUser().then(response => {
    loggedUser.value = response;
  });
  getDocument(documentId);
  fetchIntermediariesPersons();
});

onBeforeUnmount(() => {
  clearInterval(refreshInterval);
});

function getDocument(documentId) {
  DocumentService.getDocument(documentId, 'default,states')
      .then((response) => {
        document.value = response;
        canBeAccepted.value = (document.value?.state?.state === 'TO_REVIEW') || (document.value?.state?.state === 'REJECTED');
        if (response.is_generating || !response.file) {
          runRefreshInterval()
        }
      })
      .catch((error) => {
        console.log(error);
      });
}

function runRefreshInterval() {
  // Vyčistíme předchozí interval, aby nevznikaly zombie intervaly při opakovaném přegenerování
  clearInterval(refreshInterval);
  refreshInterval = setInterval(() => {
    DocumentService.getDocument(documentId, 'default,states')
        .then((response) => {
          document.value = response;
          if (!response.is_generating && response.file) {
            clearInterval(refreshInterval);
          }
        })
        .catch((error) => {
          console.log(error);
        });
  }, 3000);
}

function fetchIntermediariesPersons() {
  IntermediaryPersonsService.getAll().then(response => {
    intermediariesPeople.value = response;
  });
}

function openSignModal() {
  const {reveal, onConfirm} = createConfirmDialog(SignModal, {documentsIds: [documentId]});
  reveal();
  onConfirm(() => {
    showSuccessMessage(t('documentsDetail.documents_successfully_signed'));
    getDocument(documentId);
  });
}

function openSignSettingsModal() {
  const {reveal, onConfirm} = createConfirmDialog(SignSettingsModal, {intermediariesPeople: intermediariesPeople.value});
  reveal();
  onConfirm((data) => {
    console.log(data);
    requestSignatures(data);
  });
}

function requestSignatures(form) {
  DocumentService.requestSignatures(documentId, form)
      .then(() => {
        getDocument(documentId);
        showSuccessMessage(t('documentsDetail.signature_requested'));
      })
      .catch((error) => {
        console.log(error);
      });
}

function revokeSignature(signatureId) {
  DocumentService.revokeSignatures(documentId, [signatureId])
      .then(() => {
        getDocument(documentId);
        showSuccessMessage(t('documentsDetail.signature_deleted'));
      })
      .catch((error) => {
        console.log(error);
      });
}

function setDocumentState(documentId, state, description = null) {
  hideModal();
  isLoading.value = true;
  let body = {
    state: state,
  }
  if (description) {
    body.description = description;
  }

  DocumentService.setDocumentState(documentId, body).then(response => {
    getDocument(documentId);
    toast.success(response.message);
  });
}

function regenerateDocument(documentId) {

  isLoading.value = true;
  isRegenerating.value = true;


  DocumentService.regenerateDocument(documentId).then(response => {
    getDocument(documentId);
    toast.success(response.message);
    isLoading.value = false;
    isRegenerating.value = false;
  });

}

function showModal() {
  modalOpen.value = true;
}

function hideModal() {
  modalOpen.value = false;
}
</script>

<template>
  <div>
    <div class="page-section">
      <div class="page-section__heading">
        <div class="row">
          <div class="col-2">
            <a @click="router.back()" class="page-section__heading__back"><i class="mdi mdi-arrow-left"></i> {{ t('general.back') }}</a>
          </div>
          <div class="col-8 text-center">
            <div class="page-section__title">
              {{ document?.name }}
            </div>
          </div>
          <div class="col-2" v-if="document && document.signature_current_user && document.signature_current_user.state === 'PENDING'">
            <Button :label="t('documentsDetail.sign_document2')" @click="openSignModal"/>
          </div>
        </div>
      </div>

      <div class="page-section__content">
        <!-- Warn when the document was generated from an older version of the template -->
        <div v-if="document?.is_outdated" class="alert alert-warning mb-3">
          <i class="mdi mdi-alert-circle-outline me-1"></i>
          {{ t('documentsDetail.generated_from_outdated_template') }}
        </div>
        <div class="mb-4">
          <div class="row">
            <div class="col-12 col-md-6">
              <div class="card shadow bg-light mb-3">
                <div class="card-body">
                  <div class="mb-3">
                    <div class="d-flex justify-content-between border-bottom py-2">
                      <span>{{ t('documentsDetail.title') }}:</span>
                      <span>{{ document?.name }}</span>
                    </div>
                    <div class="d-flex justify-content-between border-bottom py-2">
                      <span>{{ t('documentsDetail.document_code') }}:</span>
                      <span>{{ document?.code }}</span>
                    </div>
                    <div class="d-flex justify-content-between border-bottom py-2">
                      <span>{{ t('documentsDetail.signatures') }}:</span>
                      <div v-if="document?.signatures">
                        <div v-for="signature in document?.signatures" :key="signature" class="d-flex justify-content-end">
                          <!-- signer is a polymorphic relation on BE and comes back as null when the signing
                               client / intermediary person no longer exists (e.g. deleted) – fall back to a dash
                               instead of crashing the whole detail render (Sentry CLIENT-APP-FRONTEND-31) -->
                          <span>{{ signature?.signer?.label ?? '–' }} - {{ signatureStateMap[signature?.state] }}</span>
                        </div>
                      </div>
                      <div v-else class="d-flex justify-content-end">{{ t('documentsDetail.signatures_not_required') }}</div>
                    </div>
                    <div class="d-flex justify-content-between border-bottom py-2">
                      <span>{{ t('documentsDetail.created_at') }}:</span>
                      <span>{{ formatDate(document?.created_at, 'j.n.Y') }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="card shadow bg-light">
                <div class="card-body">

                  <div class="row card-title">
                    <div class="col-6">
                      <h5 class="">{{ t('documentsDetail.signatures') }}</h5>
                    </div>
                    <div class="col-6">
                      <div class="d-flex justify-content-end">
                        <Button :label="t('documentsDetail.request_signatures')" @click="openSignSettingsModal"/>
                      </div>
                    </div>
                  </div>
                  <table style="table-layout: auto; width: 100%;">
                    <tr v-for="signature in document?.signatures" :key="signature">
                      <td>{{ signature?.role === 'OWNER' ? t('documentsDetail.owner') : t('documentsDetail.responsible_person') }}</td>
                      <td>{{ signature?.signer?.label }}</td>
                      <td class="text-end"><i class="mdi mdi-trash-can-outline" @click="revokeSignature(signature.id)"></i></td>
                    </tr>
                  </table>
                </div>
              </div>


            </div>

            <div class="col-12 col-md-6">
<!--              <div v-if="canBeAccepted && loggedUser.id === document.assigned_user?.id" class="row mb-3">-->
              <!-- SUPERADMIN has same document accept/reject permissions as ADMIN -->
              <div v-if="canBeAccepted && (loggedUser?.role === 'ADMIN' || loggedUser?.role === 'SUPERADMIN' || loggedUser?.role === 'BACK_OFFICE')" class="row mb-3">
                <div class="col-12 col-md-6 mb-2">
                  <button type="button" class="btn btn-danger me-2 w-100" @click="showModal(document?.id)" :disabled="document.state.state === 'CANCELED'">{{ t('documentsDetail.reject') }}</button>
                </div>
                <div class="col-12 col-md-6 mb-2">
                  <button type="button" class="btn btn-primary me-2 w-100" @click="setDocumentState(document?.id, 'ACCEPTED')" :disabled="document.state.state === 'ACCEPTED'">{{ t('documentsDetail.accept') }}</button>
                </div>
              </div>

              <div class="row mb-3">
                <div class="card shadow bg-light">
                  <div class="card-body">
                    <h5 class="card-title">{{ t('documentsDetail.state_history') }}</h5>
                    <table style="table-layout: auto; width: 100%;">
                      <tr v-for="documentState in document?.states" :key="documentState">
                        <td>{{ formatDate(documentState.created_at, 'j.n.Y H:i') }}</td>
                        <td>{{ documentState.user.name }}</td>
                        <td class="text-end">
                          <BSTooltip v-if="documentState.description" :title="documentState.description">
                            <span class="mdi mdi-message-reply-text-outline"></span>
                          </BSTooltip>
                          {{ documentStateMap[documentState.state] }}
                        </td>
                      </tr>
                    </table>
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-12" v-if="document?.can_be_regenerated">
                  <button type="button" class="btn btn-primary me-2 w-100" @click="regenerateDocument(document?.id)" :disabled="document.state?.state === 'CANCELED'">
                    <span v-if="!isRegenerating">Přegenerovat</span>
                    <span v-if="isRegenerating"><span class="loader spinner-border text-white me-3" style="vertical-align: middle"></span>Generuji... </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="page-section__content">
        <!-- :key zajistí přemontování FileView při změně UUID souboru (např. po přegenerování) -->
        <div class="row" v-if="document && document.file">
          <FileView :key="document.file.uuid" :file="document.file"/>
        </div>
        <div class="row pt-5" v-if="!document">
          <div class="col-12 d-flex justify-content-center align-items-center">
            <div class="loader spinner-border text-primary me-3"></div>
            {{ t('documentsDetail.document_is_loading') }}
          </div>
        </div>
        <div class="row pt-5" v-if="document && !document.file">
          <div class="col-12 d-flex justify-content-center align-items-center">
            <div class="loader spinner-border text-primary me-3"></div>
            {{ t('documentsDetail.document_is_generating') }}
          </div>
        </div>
      </div>
    </div>
    <!-- modals -->
    <TransitionRoot appear :show="modalOpen" as="template">
      <Dialog as="div" class="modal show d-block" style="z-index: 1055;">
        <div class="modal-backdrop fade show"></div>
        <div class="modal-dialog modal-dialog-centered" style="z-index: 1055">
          <div class="modal-content">
            <DialogPanel class="modal-body p-4 rounded-lg shadow-lg">
              <div class="modal-header">
                <h3 class="text-lg font-bold">{{ t('documentsDetail.reject_document') }}</h3>
              </div>
              <div class="modal-body">
                <div class="mt-3">
                  <div class="row my-3">
                    <div class="col-12">
                      <label for="stateDescription" class="form-label">{{ t('documentsDetail.description') }}</label>
                      <textarea v-model="stateDescription" class="w-full form-control"/>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-12 text-end">
                      <u class="btn ms-2 text-decoration-underline" @click="hideModal()">{{ t('documentsDetail.close') }}</u>
                      <Button type="button" @click="setDocumentState(document.id, 'CANCELED', stateDescription)" :label="t('documentsDetail.reject')"></Button>
                    </div>
                  </div>
                </div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
    <!-- end modals -->
  </div>

</template>

<style scoped>

</style>
